import { Box, Button, Checkbox, Divider, HStack, Radio, RadioGroup, Stack, Text, useDisclosure } from "@chakra-ui/react";
import { useState } from "react";
import MarketingModal from "../../../components/Modals/MarketingModal";
import PrivacyModal from "../../../components/Modals/PrivacyModal";
import TermsOfUseModal from "../../../components/Modals/TermsOfUseModal";
import { colors } from "../../../styles";
import { ISignUpUser } from "../../../types";
import StepLayout from "./StepLayout";
import { SignUpStep, SignUpType } from "./types";

const USER_TYPE_OPTIONS = [
    { value: "OWNER" as const, emoji: "🧑🏻‍⚕️", label: "개국약사" },
    { value: "PHARMACIST" as const, emoji: "🧑🏻‍💼", label: "약사" },
    { value: "STUDENT" as const, emoji: "🧑🏻‍🏫", label: "약대생" },
];

interface IIntroProps {
    step: SignUpStep;
    userData: Partial<ISignUpUser>;
    setUserData: React.Dispatch<React.SetStateAction<Partial<ISignUpUser>>>;
    onNext: () => void;
}

export default function Intro({ step, userData, setUserData, onNext }: IIntroProps) {

    /* type */

    const [type, setType] = useState<SignUpType>(() => {

        const currentType = userData.type;

        if (
            currentType === "OWNER" 
                || currentType === "PHARMACIST" 
                    || currentType === "STUDENT"
        ) {
            return currentType;
        }

        return "OWNER";
    });

    /* state */

    const [isTermsAgreed, setIsTermsAgreed] = useState(false);
    const [isPrivacyAgreed, setIsPrivacyAgreed] = useState(false);
    const [isAdAgreed, setIsAdAgreed] = useState(false);

    /* modal */

    const { isOpen: isTermsOfUseModalOpen, onOpen: onOpenTermsOfUseModal, onClose: onCloseTermsOfUseModal } = useDisclosure();
    const { isOpen: isPrivacyModalOpen, onOpen: onOpenPrivacyModal, onClose: onClosePrivacyModal } = useDisclosure();
    const { isOpen: isMarketingModalOpen, onOpen: onOpenMarketingModal, onClose: onCloseMarketingModal } = useDisclosure();

    /* 모든 약관 동의 여부 */

    const isAllAgreed = isTermsAgreed && isPrivacyAgreed && isAdAgreed;

    /* 필수 약관 동의 여부 */

    const isRequiredAgreed = isTermsAgreed && isPrivacyAgreed;

    /* 모든 약관 동의 여부 토글 */

    const toggleAllAgree = () => {
        const next = !isAllAgreed;
        setIsTermsAgreed(next);
        setIsPrivacyAgreed(next);
        setIsAdAgreed(next);
    };

    /* onTypeNext */

    const onTypeNext = () => {
        
        /* 여기서 정한 type이 이후 단계 분기의 기준이 된다. */

        setUserData((prev) => ({ ...prev, type }));
        onNext();
    };

    /* onAgreeNext */

    const onAgreeNext = () => {
        if (!isRequiredAgreed) return;

        setUserData((prev) => ({
            ...prev,
            type,
            isAgree: true,
            isAdAgree: isAdAgreed,
        }));
        onNext();
    };

    /* 유형 선택 */

    if (step === "type") {
        return (
            <StepLayout titles={["소속을 알려주세요"]}>

                {/* 유형 선택 라디오 버튼 */}

                <RadioGroup width="100%" value={type} onChange={(next) => {
                    if (next === "OWNER" || next === "PHARMACIST" || next === "STUDENT") {
                        setType(next);
                    }
                }}>
                    <Stack spacing={2.5} width="100%">
                        {USER_TYPE_OPTIONS.map(({ value, emoji, label }) => (
                            <Box
                                key={value}
                                as="label"
                                cursor="pointer"
                                width="100%"
                                borderRadius={10}
                                px={5}
                                py={4}
                                bg={type === value ? colors.light : colors.g5}
                                transition="background 0.15s ease"
                            >
                                <HStack width="100%" justifyContent="space-between" align="center">
                                    <HStack spacing={2}>
                                        <Text fontSize={20}>{emoji}</Text>
                                        <Text fontSize={16} fontWeight={700} color={colors.g1}>
                                            {label}
                                        </Text>
                                    </HStack>
                                    <Radio value={value} colorScheme="blue" />
                                </HStack>
                            </Box>
                        ))}
                    </Stack>
                </RadioGroup>

                {/* 다음 버튼 */}
                
                <Button
                    width="100%"
                    height="52px"
                    backgroundColor={colors.blue}
                    color="white"
                    borderRadius={10}
                    marginTop={5}
                    onClick={onTypeNext}
                >
                    <Text fontSize={16} fontWeight={700}>다음</Text>
                </Button>

            </StepLayout>
        );
    }

    /* 약관 동의. 필수 2개가 체크되어야 다음으로 갈 수 있다. */

    return (
        <StepLayout titles={["팜챗 서비스 이용약관에", "동의해주세요."]}>
            
            <HStack>
                <Checkbox
                    onChange={toggleAllAgree}
                    colorScheme="blue"
                    size="lg"
                    marginRight={2}
                    isChecked={isAllAgreed}
                />
                <Text fontSize={16} fontWeight={700} color={colors.g1}>
                    아래 내용에 모두 동의합니다.
                </Text>
            </HStack>
            <Divider marginY={2} />
            <Stack spacing={7} width="100%">

                {/* 필수 약관 동의 */}

                <AgreeRow
                    label="[필수] 서비스 이용약관 동의"
                    isChecked={isTermsAgreed}
                    onToggle={() => setIsTermsAgreed((prev) => !prev)}
                    onOpen={onOpenTermsOfUseModal}
                />

                {/* 필수 약관 동의 */}

                <AgreeRow
                    label="[필수] 개인정보 수집 및 이용방침 동의"
                    isChecked={isPrivacyAgreed}
                    onToggle={() => setIsPrivacyAgreed((prev) => !prev)}
                    onOpen={onOpenPrivacyModal}
                />

                {/* 선택 약관 동의 */}

                <AgreeRow
                    label="[선택] 광고성 정보 수신 및 마케팅 활용 동의"
                    isChecked={isAdAgreed}
                    onToggle={() => setIsAdAgreed((prev) => !prev)}
                    onOpen={onOpenMarketingModal}
                />
            </Stack>

            {/* modal */}

            <TermsOfUseModal isOpen={isTermsOfUseModalOpen} onClose={onCloseTermsOfUseModal} />
            <PrivacyModal isOpen={isPrivacyModalOpen} onClose={onClosePrivacyModal} />
            <MarketingModal isOpen={isMarketingModalOpen} onClose={onCloseMarketingModal} />

            {/* 동의하고 가입하기 버튼 */}
            
            <Button
                width="100%"
                height="52px"
                backgroundColor={colors.blue}
                color="white"
                borderRadius={10}
                marginTop={10}
                onClick={onAgreeNext}
                disabled={!isRequiredAgreed}
            >
                <Text fontSize={16} fontWeight={700}>동의하고 가입하기</Text>
            </Button>

        </StepLayout>
    );
}

function AgreeRow({
    label,
    isChecked,
    onToggle,
    onOpen,
}: {
    label: string;
    isChecked: boolean;
    onToggle: () => void;
    onOpen: () => void;
}) {
    return (
        <HStack width="100%" justifyContent="space-between">
            <HStack spacing={5}>
                <Checkbox onChange={onToggle} colorScheme="blue" size="lg" isChecked={isChecked} />
                <Text fontSize={14} color={colors.g1}>{label}</Text>
            </HStack>
            <Box
                as="button"
                onClick={onOpen}
                cursor="pointer"
                _hover={{
                    textDecoration: "underline",
                    color: colors.blue,
                }}
            >
                <Text fontSize={14} color={colors.g2} textDecoration="underline">보기</Text>
            </Box>
        </HStack>
    );
}
